import { ToastProgrammatic as Toast } from 'buefy'
import router from '@/router'
import apolloProvider from '@/graphql'
import getAppUserInfo from '@/graphql/query/getAppUserInfo.graphql'
import initAppTenantSupport from '@/graphql/query/initAppTenantSupport.graphql'
import initDemo from '@/graphql/mutation/initDemo.graphql'
import initSubsidiaryAdmin from '@/graphql/mutation/initSubsidiaryAdmin.graphql'
import resetDemoData from '@/graphql/mutation/resetDemoData.graphql'
import signCurrentEula from '@/graphql/mutation/signCurrentEula.graphql'

import getSupportEmail from '@/graphql/query/supportEmail.graphql'

const types = {
  RESET_USER: 'RESET_USER',
  SET_USER: 'SET_USER',
  SET_APP_USER: 'SET_APP_USER',
  SET_USER_AVATAR_KEY: 'SET_USER_AVATAR_KEY',
  SET_IS_LOGGED_IN: 'SET_IS_LOGGED_IN',
  SET_SUPPORT_EMAIL: 'SET_SUPPORT_EMAIL',
  SET_SCOPE_APP_TENANT: 'SET_SCOPE_APP_TENANT',
  CLEAR_SCOPE_APP_TENANT: 'CLEAR_SCOPE_APP_TENANT',
  SET_SCOPE_USER: 'SET_SCOPE_USER',
  SET_ACTUAL_USER: 'SET_ACTUAL_USER',
  IMPERSONATE_USER: 'IMPERSONATE_USER',
  STOP_IMPERSONATION: 'STOP_IMPERSONATION',
  SET_SCOPE_PERMISSIONS: 'SET_SCOPE_PERMISSIONS',
  BECOME_ACTUAL_USER: 'BECOME_ACTUAL_USER',
  BECOME_SUPPORT_USER: 'BECOME_SUPPORT_USER',
  INIT_DEMO: 'INIT_DEMO',
  INIT_SUBSIDIARY_ADMIN: 'INIT_SUBSIDIARY_ADMIN',
  RESET_DEMO_DATA: 'RESET_DEMO_DATA',
  SIGN_CURRENT_EULA: 'SIGN_CURRENT_EULA',
  SET_SHOW_OLD_USERS: 'SET_SHOW_OLD_USERS',
  SET_IMPERSONATION_REDIRECT: 'SET_IMPERSONATION_REDIRECT',
  USER_HAS_PERMISSION: 'USER_HAS_PERMISSION',
  SET_USER_DEVICE: 'SET_USER_DEVICE',
  SET_CALIBRATION_ENABLED: 'SET_CALIBRATION_ENABLED',
  SET_IS_NEW_USER: 'SET_IS_NEW_USER',
  SET_TUTORIALS_STATUS: 'SET_TUTORIALS_STATUS',
  SET_LICENSE_TYPE_KEY: 'SET_LICENSE_TYPE_KEY' // adding because "isHomePatient" does not seem to work
}

const getDefaultState = () => {
  return {
    appUser: {}, // userInfo is not including createdAt, so setting it up
    userInfo: {},
    userAvatarKey: null,
    supportEmail: 'help@ourvisualbrain.com',
    isLoggedIn: false,
    isPatient: false,
    // userDevice is stored in a persistent cookie,
    // this value is grabbed from the cookie on login if the cookie exists
    userDevice: null,
    scopeAppTenant: null,
    // scopePermissions: [],
    actualAppUserInfo: null,
    scopeAppUserInfo: null,
    showOldUsers: true,
    isNewUser: false,
    impersonationRedirectUrl: null,
    calibrationEnabled: false,
    isHomePatient: false,
    tutorialsStatus: null,
    licenseTypeKey: null // adding because "isHomePatient" does not seem to work
  }
}

// initial state
const state = getDefaultState()

// getters
const getters = {
  appUser: state => state.appUser, // userInfo is not including createdAt, so setting it up
  isNewUser: state => state.isNewUser,
  isPatient: state => state.isPatient,
  userInfo: state => state.userInfo, // all user info from OBV on a big blob
  actualAppUserInfo: state => state.actualAppUserInfo, // all user info from OBV on a big blob
  userId: state => state.userInfo.id, // just the id
  userPermissions: state => state.userInfo.permissions, // just the permissions
  isLoggedIn: state => state.isLoggedIn,
  scopeAppTenant: state => state.scopeAppTenant,
  userDevice: state => state.userDevice,
  calibrationEnabled: state => state.calibrationEnabled,
  isHomePatient: state => (state.actualAppUserInfo === null),
  tutorialsStatus: state => state.tutorialsStatus,
  licenseTypeKey: state => state.licenseTypeKey, // adding because "isHomePatient" does not seem to work
  userAvatarKey: state => state.userAvatarKey
}

// Can a user holding `permissions` actually land on `routerPath`?
// Mirrors the ANY-of semantics used by USER_HAS_PERMISSION / authGuard.
const scopedUserCanReach = (permissions, routerPath) => {
  let needed
  try {
    needed = router.resolve(routerPath).route.meta.needsPermission
  } catch (e) {
    return true // unknown route — let the guard sort it out
  }
  if (!needed || needed.length === 0) return true
  return (permissions || []).some(p => needed.includes(p))
}

// actions
const actions = {
  reset ({ commit }) { // reset the user
    commit('types.RESET_USER')
  },
  async setUser ({ commit, dispatch }, payload) { // everything from OVB on this user
    console.log('[Vuex user/setUser] Called with payload:', payload)
    console.log('[Vuex user/setUser] Libraries:', payload.libraries)
    commit(types.SET_USER, payload)
    commit(types.SET_IS_LOGGED_IN, true)
    const library = payload.libraries[0]
    console.log('[Vuex user/setUser] Dispatching SET_WORKING_LIBRARY with:', library)
    dispatch(
      'SET_WORKING_LIBRARY',
      library,
      { root: true }
    )
    console.log('[Vuex user/setUser] Complete')
  },
  setLicenseTypeKey ({ commit }, payload) {
    commit(types.SET_LICENSE_TYPE_KEY, payload)
  },
  setUserAvatarKey ({ commit }, payload) {
    commit(types.SET_USER_AVATAR_KEY, payload)
  },
  setIsPatient ({ commit }, payload) {
    commit(types.SET_USER_IS_PATIENT, payload)
  },
  setTutorialsStatus ({ commit }, payload) {
    commit(types.SET_TUTORIALS_STATUS, payload)
  },
  async checkHousekeepingTasks ({ commit, dispatch }, payload) { // everything from OVB on this user
    // support email
    const emailResult = await apolloProvider.defaultClient
      .query({
        query: getSupportEmail,
        fetchPolicy: 'cache-first'
      })
      .catch((error) => {
        console.error('Failed to fetch support email:', error)
        return null
      })

    if (emailResult && emailResult.data) {
      const supportEmail = emailResult.data.supportEmail
      commit(types.SET_SUPPORT_EMAIL, supportEmail)
    }
  },
  setAppUser ({ commit }, payload) {
    commit(types.SET_APP_USER, payload)
  },
  setIsNewUser ({ commit }, payload) {
    commit(types.SET_IS_NEW_USER, payload)
  },
  setUserToken ({ commit }, payload) {
    commit(types.SET_USER_TOKEN, payload)
  },
  setCalibrationEnabled ({ commit }, payload) {
    commit(types.SET_CALIBRATION_ENABLED, payload)
  },
  setUserDevice ({ commit }, payload) {
    commit(types.SET_USER_DEVICE, payload)
  },
  logout ({ commit }) {
    commit(types.RESET_USER, null)
  },
  [types.SET_SCOPE_APP_TENANT]: {
    root: true,
    handler: async (store, payload) => {
      // alert(JSON.stringify(payload, null, 2))
      const appTenant = payload.appTenant || payload
      console.log('payload.routerPath: ', payload.routerPath)
      const routerPath = (payload.routerPath === 'PatientDashboard' ? 'MyDashboard' : payload.routerPath) || { name: 'ClinicDashboard' }
      const result = await apolloProvider.defaultClient
        .query({
          query: initAppTenantSupport,
          variables: {
            appTenantId: appTenant.id
          },
          fetchPolicy: 'networkOnly'
        })

      const scoped = result.data.initAppTenantSupport

      // Some tenants (notably the Anchor/OVB tenant itself) have no clinic module,
      // so scoping into them dead-ends on the no-access page. Bail out before we
      // swap identities so the admin stays where they are.
      if (!scopedUserCanReach(scoped.permissions, routerPath)) {
        Toast.open({
          duration: 5000,
          message: `${appTenant.name || 'This account'} doesn't have access to that area, so it can't be scoped into.`,
          type: 'is-warning',
          position: 'is-top'
        })
        return
      }

      store.commit(types.SET_SCOPE_USER, {
        ...scoped
      })
      store.commit(types.SET_SCOPE_APP_TENANT, appTenant)
      store.commit(types.IMPERSONATE_USER, scoped)

      // also will want to do libraries here...
      const libraryId = scoped.permissions.filter(p => p.indexOf('l:') === 0)[0].split(':')[1]
      const library = scoped.libraries.find(l => l.id === libraryId)
      store.dispatch('SET_WORKING_LIBRARY',
        library,
        { root: true }
      )
      router.push(routerPath)
    }
  },
  [types.INIT_DEMO]: {
    root: true,
    handler: async (store, payload) => {
      const result = await apolloProvider.defaultClient
        .mutate({
          mutation: initDemo,
          variables: {
            licensePackKey: payload.licensePackKey
          }
        })

      store.commit(types.SET_SCOPE_USER, {
        ...result.data.initDemo.initDemoResult.appUserAuth0Info
      })
      store.commit(types.SET_SCOPE_APP_TENANT, result.data.initDemo.initDemoResult.demoAppTenant)
      store.commit(types.IMPERSONATE_USER, result.data.initDemo.initDemoResult.appUserAuth0Info)

      // also will want to do libraries here...
      const libraryId = result.data.initDemo.initDemoResult.appUserAuth0Info.permissions.filter(p => p.indexOf('l:') === 0)[0].split(':')[1]
      const library = result.data.initDemo.initDemoResult.appUserAuth0Info.libraries.find(l => l.id === libraryId)
      store.dispatch('SET_WORKING_LIBRARY',
        library,
        { root: true }
      )
      router.push({ name: 'ClinicDashboard' })
    }
  },
  [types.INIT_SUBSIDIARY_ADMIN]: {
    root: true,
    handler: async (store, payload) => {
      const result = await apolloProvider.defaultClient
        .mutate({
          mutation: initSubsidiaryAdmin,
          variables: payload
        })

      store.commit(types.SET_SCOPE_USER, {
        ...result.data.initSubsidiaryAdmin.initSubsidiaryAdminResult.appUserAuth0Info
      })
      store.commit(types.SET_SCOPE_APP_TENANT, result.data.initSubsidiaryAdmin.initSubsidiaryAdminResult.subsidiaryAppTenant)
      store.commit(types.IMPERSONATE_USER, result.data.initSubsidiaryAdmin.initSubsidiaryAdminResult.appUserAuth0Info)

      // also will want to do libraries here...
      // console.log(JSON.stringify(result.data.initDemo.initDemoResult.appUserAuth0Info.permissions, null, 2))
      // const libraryId = result.data.initDemo.initDemoResult.appUserAuth0Info.permissions.filter(p => p.indexOf('l:') === 0)[0].split(':')[1]
      // const library = result.data.initDemo.initDemoResult.appUserAuth0Info.libraries.find(l => l.id === libraryId)
      // store.dispatch('SET_WORKING_LIBRARY',
      //   library,
      //   { root: true }
      // )
      router.push({ name: 'ClinicDashboard' })
    }
  },
  [types.IMPERSONATE_USER]: {
    root: true,
    handler: async (store, payload) => {
      // await store.commit(types.BECOME_ACTUAL_USER)
      if (['fnb-support', 'fnb-demo'].indexOf(store.state.userInfo.username) > -1) {
        await store.commit(types.BECOME_ACTUAL_USER)
      } else {
        // this would be the case where a therapist has clicked the In Clinic Play button
        // this is where we need to set the current machine if it is not set
        await store.commit(types.SET_ACTUAL_USER, store.state.userInfo)
      }

      const result = await apolloProvider.defaultClient.mutate({
        mutation: getAppUserInfo,
        variables: payload
      })
      const redirect = {
        name: router.currentRoute.name,
        params: router.currentRoute.params,
        query: router.currentRoute.query
      }
      await store.commit(types.SET_IMPERSONATION_REDIRECT, redirect)
      await store.commit(types.IMPERSONATE_USER, result.data.getAppUserInfo)
      const routePath = result.data.getAppUserInfo.homePath === 'PatientDashboard' ? 'MyDashboard' : result.data.getAppUserInfo.homePath
      router.push({ name: routePath }).catch(() => {
        router.push('/').catch(() => {})
      })
    }
  },
  [types.STOP_IMPERSONATION]: {
    root: true,
    handler: async (store, payload) => {
      store.commit(types.STOP_IMPERSONATION)

      // Drop stale data from the impersonated context. clearStore (not resetStore)
      // because refetching the still-mounted patient queries under the restored
      // identity just throws "Invariant Violation: 16" — we navigate away next
      // anyway, and the destination remounts its own queries.
      try {
        await apolloProvider.defaultClient.clearStore()
      } catch (e) {
        // safe to ignore — cache is being discarded regardless
      }

      // Re-set working library from restored user
      const restoredUser = store.state.userInfo
      if (restoredUser && restoredUser.libraries && restoredUser.libraries.length > 0) {
        const libraryId = restoredUser.permissions
          ? restoredUser.permissions.filter(p => p.indexOf('l:') === 0).map(p => p.split(':')[1])[0]
          : null
        const library = libraryId
          ? restoredUser.libraries.find(l => l.id === libraryId) || restoredUser.libraries[0]
          : restoredUser.libraries[0]
        store.dispatch('SET_WORKING_LIBRARY', library, { root: true })
      }

      // A restored scoped user can't reach OvbDashboard, so fall back into the clinic.
      const fallback = store.state.scopeAppTenant ? { name: 'ClinicDashboard' } : { name: 'OvbDashboard' }
      const redirect = store.state.impersonationRedirectUrl || fallback
      store.commit(types.SET_IMPERSONATION_REDIRECT, null)
      router.push(redirect).catch(() => router.push(fallback).catch(() => {}))
    }
  },
  [types.CLEAR_SCOPE_APP_TENANT]: {
    root: true,
    handler: async (store, payload) => {
      store.commit(types.CLEAR_SCOPE_APP_TENANT)
      // Reset Apollo cache to clear stale data from scoped context
      try {
        await apolloProvider.defaultClient.resetStore()
      } catch (e) {
        // resetStore can throw if active queries fail to refetch — safe to ignore
      }
      if (store.state.userInfo && store.state.userInfo.permissionKey === 'SuperAdmin') {
        router.push({ name: 'OvbDashboard' })
      } else {
        if (router.currentRoute.name === 'ClinicDashboard') {
          // store.commit('app/refreshKey', null, { root: true })
          router.go()
        } else {
          router.push({ name: 'ClinicDashboard' })
        }
      }
    }
  },
  [types.RESET_DEMO_DATA]: {
    root: true,
    handler: async (store, payload) => {
      apolloProvider.defaultClient
        .mutate({
          mutation: resetDemoData,
          variables: {
            appTenantId: store.state.scopeAppTenant.id
          }
        })
        .then(() => {
          alert('demo data has been reset')
          store.commit('app/refreshKey', null, { root: true })
        })
    }
  },
  [types.SIGN_CURRENT_EULA]: {
    root: true,
    handler: async (store, payload) => {
      return apolloProvider.defaultClient
        .mutate({
          mutation: signCurrentEula,
          variables: payload
        })
    }
  },
  [types.SET_SHOW_OLD_USERS]: {
    root: true,
    handler: async (store, payload) => {
      store.commit('SET_SHOW_OLD_USERS', payload)
    }
  },
  [types.USER_HAS_PERMISSION]: {
    root: true,
    handler: async (store, permissionKey) => {
      const permissionKeys = Array.isArray(permissionKey) ? permissionKey : [permissionKey]
      if (permissionKeys.length === 0) return true

      if (!store.state) return false
      if (!store.state.userInfo) return false
      if (!store.state.userInfo.permissions) return false

      const userPermissions = store.state.userInfo.permissions
      const found = userPermissions.some(r => permissionKeys.includes(r))
      return found
    }
  }
}

// mutations
const mutations = {
  [types.RESET_USER] (state) {
    Object.assign(state, getDefaultState())
  },
  [types.SET_APP_USER] (state, payload) {
    state.appUser = payload
  },
  [types.SET_IS_NEW_USER] (state, payload) {
    state.isNewUser = payload
  },
  [types.SET_USER] (state, userInfo) {
    if (userInfo.permissions.includes('m:patient')) {
      state.isPatient = true
    }
    state.userInfo = userInfo
  },
  [types.SET_LICENSE_TYPE_KEY] (state, payload) {
    state.licenceTypeKey = payload
  },
  [types.SET_USER_AVATAR_KEY] (state, payload) {
    state.userAvatarKey = payload
  },
  [types.SET_IS_LOGGED_IN] (state, payload) {
    state.isLoggedIn = payload
  },
  [types.SET_USER_DEVICE] (state, payload) {
    state.userDevice = payload
  },
  [types.SET_TUTORIALS_STATUS] (state, payload) {
    state.tutorialsStatus = payload
  },
  [types.SET_SUPPORT_EMAIL] (state, payload) {
    state.supportEmail = payload
  },
  [types.SET_SCOPE_APP_TENANT] (state, payload) {
    state.actualAppUserInfo = state.actualAppUserInfo || state.userInfo
    state.scopeAppTenant = payload
  },
  [types.CLEAR_SCOPE_APP_TENANT] (state) {
    state.userInfo = state.actualAppUserInfo
    state.scopeAppTenant = null
    state.scopeAppUserInfo = null
    state.actualAppUserInfo = null
  },
  [types.BECOME_ACTUAL_USER]: (state) => {
    if (state.actualAppUserInfo) {
      state.userInfo = state.actualAppUserInfo
    }
  },
  [types.SET_ACTUAL_USER]: (state, payload) => {
    state.actualAppUserInfo = payload
  },
  [types.BECOME_SUPPORT_USER]: (state) => {
    state.userInfo = state.scopeAppUserInfo
  },
  [types.SET_SCOPE_USER]: (state, payload) => {
    state.scopeAppUserInfo = payload
  },
  [types.IMPERSONATE_USER] (state, payload) {
    state.actualAppUserInfo = state.actualAppUserInfo || state.userInfo
    state.userInfo = payload
  },
  [types.SET_IMPERSONATION_REDIRECT] (state, payload) {
    state.impersonationRedirectUrl = payload
  },
  [types.STOP_IMPERSONATION] (state) {
    // If a tenant was being shadowed before impersonation started (an admin
    // scoped into a clinic, then hit In-Office Play), step back into that
    // scoped context instead of all the way out to the admin identity.
    // actualAppUserInfo stays put so Exit Shadow can still unwind fully.
    if (state.scopeAppTenant && state.scopeAppUserInfo) {
      state.userInfo = state.scopeAppUserInfo
      return
    }

    // Otherwise restore the actual admin/original user identity
    // and clear all scope context.
    if (state.actualAppUserInfo) {
      state.userInfo = state.actualAppUserInfo
    }
    state.actualAppUserInfo = null
    state.scopeAppUserInfo = null
    state.scopeAppTenant = null
  },
  [types.SET_SHOW_OLD_USERS] (state, payload) {
    state.showOldUsers = payload
  },
  [types.SET_CALIBRATION_ENABLED] (state, payload) {
    state.calibrationEnabled = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
